import { create } from "zustand";
import { persist } from "zustand/middleware";

interface UIState {
  // Sidebar
  sidebarCollapsed: boolean;
  sidebarOpen: boolean; // For mobile

  // Theme
  theme: "light" | "dark" | "system";

  // Chat
  chatAutoPopup: boolean; // 채팅 메시지 수신 시 자동 팝업

  // Actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarOpen: (open: boolean) => void;
  setTheme: (theme: "light" | "dark" | "system") => void;
  setChatAutoPopup: (enabled: boolean) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      sidebarOpen: false, // Note: Not persisted - mobile sidebar should always start closed
      theme: "system",
      chatAutoPopup: false,

      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setSidebarCollapsed: (collapsed: boolean) =>
        set({ sidebarCollapsed: collapsed }),

      setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),

      setChatAutoPopup: (enabled: boolean) => set({ chatAutoPopup: enabled }),

      setTheme: (theme: "light" | "dark" | "system") => {
        set({ theme });

        // Apply theme to document
        const root = window.document.documentElement;
        root.classList.remove("light", "dark");

        if (theme === "system") {
          const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
            .matches
            ? "dark"
            : "light";
          root.classList.add(systemTheme);
        } else {
          root.classList.add(theme);
        }
      },
    }),
    {
      name: "ui-storage",
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        chatAutoPopup: state.chatAutoPopup,
        // sidebarOpen is intentionally NOT persisted - mobile sidebar should always start closed
      }),
      // Ensure sidebarOpen is always false on load (fix for users with stale localStorage)
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...(persistedState as Partial<UIState>),
        sidebarOpen: false, // Always reset to false
      }),
    }
  )
);

// Apply theme on load
if (typeof window !== "undefined") {
  const theme = useUIStore.getState().theme;
  const root = window.document.documentElement;
  root.classList.remove("light", "dark");

  if (theme === "system") {
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
    root.classList.add(systemTheme);
  } else {
    root.classList.add(theme);
  }

  // Listen for system theme changes
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", (e) => {
      if (useUIStore.getState().theme === "system") {
        const root = window.document.documentElement;
        root.classList.remove("light", "dark");
        root.classList.add(e.matches ? "dark" : "light");
      }
    });
}
